import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import TitleCard from './TitleCard';
import Button from './Button';
import Pagination from './Pagination';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Footer from './Footer';
import DataPlataformSearch from './DataPlataformSearch';
import { ChevronRight, Building2, CalendarRange, RefreshCw, Download } from 'lucide-react';
import { DATASET_NAV, getDatasetMeta } from '../Screens/DataPlataformScreens/datasets';

const ALL_STATES_VALUE = '__ALL_ESTADOS__';

// Bloco de seção usado nas informações descritivas da base
function DatasetSection({ title, children }) {
    return (
        <section className='dataset-section'>
            <h2 className='dataset-section-title'>{title}</h2>
            {children}
        </section>
    );
}


// Função utilitária para abreviar valores grandes
function abreviarNumero(valor) {
    if (valor >= 1e12) return (valor / 1e12).toFixed(2).replace('.', ',') + ' Tri';
    if (valor >= 1e9) return (valor / 1e9).toFixed(2).replace('.', ',') + ' Bi';
    if (valor >= 1e6) return (valor / 1e6).toFixed(2).replace('.', ',') + ' Mi';
    if (valor >= 1e3) return (valor / 1e3).toFixed(2).replace('.', ',') + ' mil';
    return valor?.toLocaleString('pt-BR');
}

class DataPlataformListPage extends Component {
    state = {
        exercicios: [],
        exerciciosFiltrados: [],
        ufs: [],
        ufsFiltrados: [],
        contas: [],
        contasAll: [],
        contasPage: 1,
        contasHasMore: true,
        contasLoading: false,
        contasSearchTerm: '',
        ufsPage: 1,
        ufsHasMore: true,
        ufsLoading: false,
        exerciciosSelecionados: [],
        ufsSelecionadas: [],
        contasSelecionadas: [],
        tiposDespesa: [],
        tiposDespesaSelecionados: [],
        searchLoading: false,
        results: [],
        count: 0,
        page: 1,
        pageSize: 25,
        chartData: null,
        chartLoading: false,
        showEmailModal: false,
        emailToSend: '',
        emailSending: false,
        emailError: '',
        emailSuccess: false,
        buscou: false,
        ufsSearchTerm: '',
        descricaoExpandida: false,
    };

    componentDidMount() {
        this.fetchFiltros();
        if (this.props.enableTipoDespesaFilter) {
            this.fetchTiposDespesaOptions();
        }
    }

    getOptionValue = (option) => ((option && typeof option === 'object') ? option.value : option);

    mapContaOption = (conta) => {
        const value = conta?.codigo ?? conta?.value;
        const descricao = conta?.descricao ?? conta?.label ?? '';
        const label = value && descricao
            ? `${value} - ${descricao}`
            : (descricao || String(value || ''));
        return { value, label };
    };

    resolveHasMore = (data) => {
        if (typeof data?.next !== 'undefined') return Boolean(data.next);
        if (typeof data?.hasMore !== 'undefined') return Boolean(data.hasMore);
        return Array.isArray(data?.results) && data.results.length > 0;
    };

    getContasMode = () => this.props.contasMode || 'paginated';

    getContaQueryParam = () => this.props.contaQueryParam || 'codigo_conta';

    fetchContas = ({ page = 1, searchTerm = '', append = false } = {}) => {
        this.setState({ contasLoading: true });
        const { api } = this.props;

        if (this.getContasMode() === 'flat') {
            // Lista já carregada: filtra no cliente
            if (this.state.contasAll.length > 0 && !append) {
                const term = (searchTerm || '').toLowerCase();
                const contas = term
                    ? this.state.contasAll.filter(c => c.label.toLowerCase().includes(term))
                    : this.state.contasAll;
                this.setState({
                    contas,
                    contasLoading: false,
                    contasHasMore: false,
                    contasSearchTerm: searchTerm,
                });
                return;
            }

            fetch(api.contas)
                .then(res => res.json())
                .then(data => {
                    const raw = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
                    const contasAll = raw.map(item => (
                        typeof item === 'string' || typeof item === 'number'
                            ? { value: item, label: String(item) }
                            : this.mapContaOption(item)
                    )).filter(c => c.value != null && c.value !== '');

                    const term = (searchTerm || '').toLowerCase();
                    const contas = term
                        ? contasAll.filter(c => c.label.toLowerCase().includes(term))
                        : contasAll;

                    this.setState({
                        contasAll,
                        contas,
                        contasLoading: false,
                        contasPage: 1,
                        contasHasMore: false,
                        contasSearchTerm: searchTerm,
                    });
                })
                .catch(() => this.setState({ contasLoading: false }));
            return;
        }

        const params = new URLSearchParams();
        params.append('page', page);
        if (searchTerm) {
            params.append('q', searchTerm);
        }

        fetch(`${api.contas}/?${params.toString()}`)
            .then(res => res.json())
            .then(data => {
                const nextContas = Array.isArray(data.results)
                    ? data.results.map(this.mapContaOption)
                    : [];

                this.setState(prevState => {
                    const contas = append
                        ? [...prevState.contas, ...nextContas].filter((conta, idx, arr) => arr.findIndex(c => c.value === conta.value) === idx)
                        : nextContas;

                    return {
                        contas,
                        contasLoading: false,
                        contasPage: page,
                        contasHasMore: this.resolveHasMore(data),
                        contasSearchTerm: searchTerm,
                    };
                });
            })
            .catch(() => this.setState({ contasLoading: false }));
    };

    fetchTiposDespesaOptions = () => {
        const { api, tipoDespesaOptions = [] } = this.props;

        if (tipoDespesaOptions.length > 0) {
            this.setState({
                tiposDespesa: tipoDespesaOptions.map(tipo => ({ value: tipo, label: tipo }))
            });
            return;
        }

        fetch(`${api.search}/?page=1`)
            .then(res => res.json())
            .then(data => {
                const tipos = Array.from(
                    new Set((data?.results || []).map(item => item?.tipo_despesa).filter(Boolean))
                ).map(tipo => ({ value: tipo, label: tipo }));

                this.setState({ tiposDespesa: tipos });
            })
            .catch(() => { });
    };

    fetchFiltros = () => {
        const { api } = this.props;
        // Exercícios
        fetch(api.exercicios)
            .then(res => res.json())
            .then(data => this.setState({ exercicios: data, exerciciosFiltrados: data }))
            .catch(() => { });
        // UFs
        fetch(api.ufs)
            .then(res => res.json())
            .then(data => {
                if (typeof api.ufs === 'string' && api.ufs.includes('filtro-uf')) {
                    const ufOptions = Array.isArray(data)
                        ? data.map(uf => ({ value: uf, label: uf }))
                        : [];
                    const withAllStates = [
                        { value: ALL_STATES_VALUE, label: 'Todos os estados' },
                        ...ufOptions
                    ];
                    this.setState({ ufs: withAllStates, ufsFiltrados: withAllStates });
                } else {
                    // this.setState({ ufs: Array.isArray(data.results) ? data.results : [], ufsFiltrados: Array.isArray(data.results) ? data.results : [] });
                    const ufs = Array.isArray(data.results) ? data.results.map(uf => ({
                        value: uf.value,
                        label: uf.label,
                        parent: uf.parent || null
                    })) : [];
                    this.setState({ ufs: ufs, ufsFiltrados: ufs });
                }

            })
            .catch(() => { });
        // Contas
        this.fetchContas({ page: 1 });
    };

    buildQueryString = () => {
        const { exerciciosSelecionados, contasSelecionadas, ufsSelecionadas, tiposDespesaSelecionados } = this.state;
        const contaQueryParam = this.getContaQueryParam();
        const params = [];
        exerciciosSelecionados.forEach(exercicio => {
            params.push(`exercicio=${encodeURIComponent(exercicio)}`);
        });
        contasSelecionadas.forEach(conta => {
            params.push(`${contaQueryParam}=${encodeURIComponent(conta.value)}`);
        });
        tiposDespesaSelecionados.forEach(tipo => {
            params.push(`tipo_despesa=${encodeURIComponent(this.getOptionValue(tipo))}`);
        });
        if (typeof this.props.api.ufs === 'string' && this.props.api.ufs.includes('filtro-uf')) {
            const hasAllStates = ufsSelecionadas.some(localidade => this.getOptionValue(localidade) === ALL_STATES_VALUE);
            if (hasAllStates) {
                return params.join('&');
            }
            ufsSelecionadas.forEach(localidade => {
                params.push(`localidade=${encodeURIComponent(this.getOptionValue(localidade))}`);
            });
        } else {
            ufsSelecionadas.forEach(localidade => {
                params.push(`localidade=${encodeURIComponent(localidade.value)}`);
            });
        }

        return params.join('&');
    };

    handleSearch = (page = 1) => {
        const { api } = this.props;
        const queryString = this.buildQueryString();
        let url = api.search;
        let params = [];
        if (queryString) params.push(queryString);
        params.push(`page=${page}`);
        url += params.length ? `/?${params.join('&')}` : '';
        this.setState({ searchLoading: true, buscou: true });

        if (page === 1) {
            this.fetchGrafico();
        }

        fetch(url)
            .then(res => res.json())
            .then(data => {
                this.setState({
                    results: data.results,
                    count: data.count,
                    page,
                    searchLoading: false
                });
            })
            .catch(() => {
                this.setState({ searchLoading: false });
            });
    };

    handleUfsLoadMore = () => {
        if (this.state.ufsLoading || !this.state.ufsHasMore) return;
        this.setState({ ufsLoading: true });
        const nextPage = this.state.ufsPage + 1;
        const { api } = this.props;
        const { ufsSearchTerm } = this.state;
        let url = `${api.ufs}/?page=${nextPage}`;
        if (ufsSearchTerm) {
            url += `&q=${encodeURIComponent(ufsSearchTerm)}`;
        }
        fetch(url)
            .then(res => res.json())
            .then(data => {
                const newUfs = Array.isArray(data.results)
                    ? data.results.map(uf => ({
                        value: uf.value,
                        label: uf.label,
                        parent: uf.parent || null
                    }))
                    : [];
                this.setState(prevState => ({
                    ufs: [...prevState.ufs, ...newUfs],
                    ufsFiltrados: [...prevState.ufs, ...newUfs],
                    ufsPage: nextPage,
                    ufsHasMore: newUfs.length > 0,
                    ufsLoading: false
                }));
            })
            .catch(() => this.setState({ ufsLoading: false }));
    };

    handleContasLoadMore = () => {
        if (this.getContasMode() === 'flat') return;
        if (this.state.contasLoading || !this.state.contasHasMore) return;
        const nextPage = this.state.contasPage + 1;
        this.fetchContas({ page: nextPage, searchTerm: this.state.contasSearchTerm, append: true });
    };

    handleUfsSearch = (searchTerm) => {
        this.setState({ ufsSearchTerm: searchTerm });
        if (typeof this.props.api.ufs === 'string' && this.props.api.ufs.includes('filtro-uf')) {
            if (!searchTerm) {
                this.setState(prevState => ({
                    ufsFiltrados: prevState.ufs,
                    ufsLoading: false
                }));
                return;
            }

            this.setState(prevState => ({
                ufsFiltrados: prevState.ufs.filter(
                    ex =>
                        ex.value === ALL_STATES_VALUE ||
                        ex.label.toString().toLowerCase().includes(searchTerm.toLowerCase())
                ),
                ufsLoading: false
            }));
        } else {
            this.setState({ ufsLoading: true });
            const baseUrl = this.props.api.ufs;
            const url = searchTerm
                ? `${baseUrl}?q=${encodeURIComponent(searchTerm)}`
                : baseUrl;
            fetch(url)
                .then(res => res.json())
                .then(data => {
                    this.setState({
                        ufsFiltrados: data.results || [],
                        ufsHasMore: data.next !== undefined ? data.next : (data.results && data.results.length > 0),
                        ufsLoading: false,
                        ufsPage: 1
                    });
                })
                .catch(() => this.setState({ ufsLoading: false }));
        }

    };

    handleExerciciosSearch = (searchTerm) => {
        // Se não houver busca, mostra todos
        if (!searchTerm) {
            this.setState(prevState => ({
                exerciciosFiltrados: prevState.exercicios,
                exerciciosLoading: false
            }));
            return;
        }
        // Filtra localmente considerando que cada exercício é um número (ano)
        this.setState(prevState => ({
            exerciciosFiltrados: prevState.exercicios.filter(
                ex =>
                    ex.toString().includes(searchTerm)
            ),
            exerciciosLoading: false
        }));
    };

    handleContasSearch = (searchTerm) => {
        this.fetchContas({ page: 1, searchTerm, append: false });
    };

    fetchGrafico = () => {
        const { api } = this.props;
        const queryString = this.buildQueryString();
        let url_grafico = api.grafico;
        url_grafico += queryString ? `/?${queryString}` : '';
        this.setState({ chartLoading: true });

        fetch(url_grafico)
            .then(res => res.json())
            .then(data => {
                this.setState({ chartData: data, chartLoading: false });
            })
            .catch(() => {
                this.setState({ chartLoading: false });
            });
    };

    getChartDataArray() {
        const { chartData } = this.state;
        if (!chartData) return [];
        const { x_axis, y_axis } = chartData;
        const ufs = Object.keys(y_axis);
        return x_axis.map((ano, idx) => {
            const obj = { ano };
            ufs.forEach(uf => {
                obj[uf] = y_axis[uf][idx];
            });
            return obj;
        });
    }

    handlePageChange = (page) => {
        this.handleSearch(page);
    };

    openEmailModal = () => this.setState({ showEmailModal: true, emailToSend: '', emailError: '', emailSuccess: false });
    closeEmailModal = () => this.setState({ showEmailModal: false, emailError: '', emailSuccess: false });

    handleEmailChange = (e) => this.setState({ emailToSend: e.target.value, emailError: '', emailSuccess: false });

    sendEmail = () => {
        const { emailToSend } = this.state;
        if (!emailToSend || !/\S+@\S+\.\S+/.test(emailToSend)) {
            this.setState({ emailError: 'Digite um e-mail válido.' });
            return;
        }
        this.setState({ emailSending: true, emailError: '', emailSuccess: false });

        const { api } = this.props;
        const queryString = this.buildQueryString();
        let url_email = api.email;
        url_email += queryString ? `/?${queryString}` : '/';

        fetch(url_email, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: emailToSend })
        })
            .then(res => {
                if (res.status !== 200) {
                    console.error('Erro ao enviar e-mail:', res);
                    this.setState({ emailSending: false, emailSuccess: false, emailError: 'Erro ao enviar e-mail. Tente novamente' });
                } else {
                    this.setState({ emailSending: false, emailSuccess: true, emailError: '' });
                }
                return res.json();
            })
            .then(data => {
                console.log(data)
            })
            .catch((error) => {
                console.error('Erro ao enviar e-mail:', error);
                this.setState({ emailSending: false, emailSuccess: false, emailError: error.message || 'Erro ao enviar e-mail. Tente novamente' });
            });
    };

    renderEmailModal() {
        const { showEmailModal, emailToSend, emailSending, emailError, emailSuccess } = this.state;
        if (!showEmailModal) return null;
        return (
            <div style={{
                position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
            }}>
                <div style={{
                    background: '#fff', borderRadius: 12, padding: 32, minWidth: 420, width: '100%', maxWidth: 520, boxShadow: '0 4px 24px #0002',
                    display: 'flex', flexDirection: 'column', alignItems: 'center'
                }}>
                    <span style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Enviar por E-mail</span>
                    <input
                        type="email"
                        placeholder="Digite seu e-mail"
                        value={emailToSend}
                        onChange={this.handleEmailChange}
                        style={{
                            width: '100%', padding: 10, borderRadius: 6, border: '1px solid #E8EAF4',
                            marginBottom: 12, fontSize: 14
                        }}
                        disabled={emailSending || emailSuccess}
                    />
                    {emailError && <span style={{ color: '#d32f2f', marginBottom: 8 }}>{emailError}</span>}
                    {emailSuccess && <span style={{ color: '#388e3c', marginBottom: 8 }}>O seu email está sendo processado e você em breve o receberá.<br/>Lembre de verificar sua caixa de spam.</span>}
                    <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                        <Button
                            label={emailSending ? "Enviando..." : "Enviar"}
                            variant="primary"
                            onClick={this.sendEmail}
                            disabled={emailSending || emailSuccess}
                        />
                        <Button
                            label="Cancelar"
                            variant="secondary"
                            onClick={this.closeEmailModal}
                            disabled={emailSending}
                        />
                    </div>
                </div>
            </div>
        );
    }

    renderChart() {
        const { chartData, chartLoading, buscou } = this.state;
        if (chartLoading) {
            return (
                <div style={{
                    width: '100%',
                    height: 350,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <span
                        style={{
                            display: 'inline-block',
                            width: 32,
                            height: 32,
                            border: '4px solid #1976d2',
                            borderTop: '4px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            marginBottom: 12
                        }}
                    />
                    <span style={{ fontSize: 16, color: '#37428E', marginLeft: 16 }}>Carregando gráfico...</span>
                    <style>
                        {`
                        @keyframes spin {
                            0% { transform: rotate(0deg);}
                            100% { transform: rotate(360deg);}
                        }
                    `}
                    </style>
                </div>
            );
        }
        if (!buscou) {
            return null;
        }
        if (
            !chartData ||
            !Array.isArray(chartData.x_axis) ||
            chartData.x_axis.length === 0 ||
            !chartData.y_axis ||
            Object.keys(chartData.y_axis).length === 0
        ) {
            return (
                <div style={{ width: '100%', height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 18 }}>
                    Nenhum dado disponível para o gráfico.
                </div>
            );
        }

        const data = this.getChartDataArray();
        const ufs = Object.keys(chartData.y_axis);

        return (
            <div style={{ width: '100%', height: 350, margin: '32px 0', overflow: 'visible' }}>
                <ResponsiveContainer>
                    <LineChart data={data}>
                        <XAxis dataKey="ano" />
                        <YAxis
                            width={90}
                            tickFormatter={abreviarNumero}
                        />
                        <Tooltip
                            formatter={valor => abreviarNumero(valor)}
                        />
                        <Legend />
                        {ufs.map((uf, idx) => (
                            <Line
                                key={uf}
                                type="monotone"
                                dataKey={uf}
                                stroke={`hsl(${(idx * 40) % 360}, 70%, 40%)`}
                                dot={false}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    }

    renderTable() {
        const { columns } = this.props;
        const { results, searchLoading, buscou } = this.state;

        if (searchLoading) {
            return (
                <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>
                    Carregando dados...
                </div>
            );
        }

        if (buscou && (!results || results.length === 0)) {
            return (
                <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>
                    Nenhum resultado encontrado.
                </div>
            );
        }

        if (!buscou) {
            return null;
        }

        const headerContentStyle = {
            display: 'block',
            width: 'calc(100% - 8px)',
            margin: '0 auto',
            background: '#E8EAF4',
            // borderRadius: 12,
            padding: '6px 0 6px 16px',
            fontWeight: 600,
            fontSize: 14,
            color: '#3C3C3C'
        };

        const headerContentStyleCenter = {
            display: 'block',
            width: 'calc(100% - 8px)',
            margin: '0 auto',
            background: '#E8EAF4',
            // borderRadius: 12,
            padding: '6px 6px 6px 6px', // padding menor para UF
            fontWeight: 600,
            fontSize: 14,
            color: '#3C3C3C'
        };

        const headerStyle = {
            padding: '14px 10px',
            textAlign: 'left',
            borderBottom: '1px solid #E8EAF4',
            background: 'transparent'
        };
        const headerStyleRight = {
            ...headerStyle,
            textAlign: 'right',
        };
        const cellStyle = {
            padding: '12px 10px',
            color: '#3C3C3C',
            fontSize: 14,
            background: '#fff',
            borderBottom: '1px solid #E8EAF4'
        };
        const cellStyleRight = {
            ...cellStyle,
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums'
        };

        return (
            <table
                style={{
                    width: '100%',
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                    // border: '1px solid #E8EAF4',
                    borderRadius: 20,
                    overflow: 'hidden',
                    background: '#fff',
                    padding: '40px 40px 0px 40px',
                }}
            >
                <thead>
                    <tr>
                        {columns.map((col, idx) => (
                            <th
                                key={col.key}
                                style={col.align === 'right' ? headerStyleRight : headerStyle}
                            >
                                <span style={col.align === 'center' ? headerContentStyle : headerContentStyleCenter}>
                                    {col.icon}{col.label}
                                </span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {results.map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #E8EAF4' }}>
                            {columns.map(col => (
                                <td
                                    key={col.key}
                                    style={col.align === 'right' ? cellStyleRight : cellStyle}
                                >
                                    {col.format
                                        ? col.format(item[col.key])
                                        : item[col.key]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    getMeta() {
        // A prop `meta` permite sobrescrever o registro central quando necessário
        return this.props.meta || getDatasetMeta(this.props.datasetKey);
    }

    renderBreadcrumb() {
        const { title } = this.props;
        const linkStyle = { color: '#818181', fontSize: 14, textDecoration: 'none' };

        return (
            <nav className='dataset-breadcrumb' aria-label="Você está aqui">
                <img src="/images/icons/home.svg" width="16" alt="" />
                <Link to="/" style={linkStyle}>Início</Link>
                <ChevronRight size={16} color="#818181" />
                <Link to="/data-platform" style={linkStyle}>Plataforma de Dados</Link>
                <ChevronRight size={16} color="#818181" />
                <span style={{ color: '#4654A3', fontSize: 14 }}>{title}</span>
            </nav>
        );
    }

    renderDescricao(meta) {
        const { descricaoExpandida } = this.state;
        const paragrafos = meta?.descricao;
        if (!Array.isArray(paragrafos) || paragrafos.length === 0) return null;

        const visiveis = descricaoExpandida ? paragrafos : paragrafos.slice(0, 1);
        const precisaExpandir = paragrafos.length > 1;

        return (
            <div className='dataset-description'>
                {visiveis.map((paragrafo, idx) => (
                    <p
                        key={idx}
                        className={!descricaoExpandida && precisaExpandir ? 'dataset-description-clamp' : ''}
                        dangerouslySetInnerHTML={{ __html: paragrafo }}
                    />
                ))}
                {precisaExpandir && (
                    <button
                        type="button"
                        className='dataset-link-button'
                        onClick={() => this.setState({ descricaoExpandida: !descricaoExpandida })}
                        aria-expanded={descricaoExpandida}
                    >
                        {descricaoExpandida ? 'Ler menos' : 'Ler mais'}
                    </button>
                )}
            </div>
        );
    }

    renderHeader(meta) {
        const { title, iconLeft, label } = this.props;
        const cobertura = meta?.coberturaTemporal;

        return (
            <header className='dataset-header'>
                {this.renderBreadcrumb()}
                <TitleCard iconLeft={iconLeft} label={label} style={{ marginBottom: 12 }} />
                <h1 className='dataset-title'>
                    {title}
                    {meta?.tituloComplemento && (
                        <span className='dataset-title-suffix'> — {meta.tituloComplemento}</span>
                    )}
                </h1>
                {this.renderDescricao(meta)}
                {(meta?.organizacao || cobertura || meta?.periodicidade) && (
                    <div className='dataset-meta-grid'>
                        {meta?.organizacao && (
                            <div className='dataset-meta-item'>
                                <span className='dataset-meta-label'>
                                    <Building2 size={16} color="#4654A3" /> Organização responsável
                                </span>
                                <span className='dataset-meta-value'>{meta.organizacao}</span>
                            </div>
                        )}
                        {cobertura && (
                            <div className='dataset-meta-item'>
                                <span className='dataset-meta-label'>
                                    <CalendarRange size={16} color="#4654A3" /> Cobertura temporal
                                </span>
                                <span className='dataset-meta-value'>{cobertura.inicio}–{cobertura.fim}</span>
                                <div className='dataset-coverage-track' aria-hidden="true">
                                    <span className='dataset-coverage-dot' />
                                    <span className='dataset-coverage-dot' />
                                </div>
                            </div>
                        )}
                        {meta?.periodicidade && (
                            <div className='dataset-meta-item'>
                                <span className='dataset-meta-label'>
                                    <RefreshCw size={16} color="#4654A3" /> Periodicidade
                                </span>
                                <span className='dataset-meta-value'>{meta.periodicidade}</span>
                            </div>
                        )}
                    </div>
                )}
            </header>
        );
    }

    renderSidebar() {
        const { datasetKey } = this.props;

        return (
            <aside className='dataset-sidebar'>
                <span className='dataset-sidebar-title'>Bases disponíveis</span>
                {DATASET_NAV.map(grupo => (
                    <div key={grupo.group} className='dataset-sidebar-group'>
                        <span className='dataset-sidebar-group-label'>{grupo.group}</span>
                        {grupo.items.map(item => (
                            <Link
                                key={item.key}
                                to={item.path}
                                className={`dataset-nav-item${item.key === datasetKey ? ' active' : ''}`}
                                aria-current={item.key === datasetKey ? 'page' : undefined}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </div>
                ))}
            </aside>
        );
    }

    renderInfoSections(meta) {
        if (!meta) return null;
        const { fontesOriginais, atualizacao, periodicidade, dicionario } = meta;
        const temFontes = Array.isArray(fontesOriginais) && fontesOriginais.length > 0;
        const temDicionario = Array.isArray(dicionario?.arquivos) && dicionario.arquivos.length > 0;

        return (
            <div className='dataset-info'>
                {temFontes && (
                    <DatasetSection title={fontesOriginais.length > 1 ? 'Fontes originais' : 'Fonte original'}>
                        <ul className='dataset-source-list'>
                            {fontesOriginais.map((fonte, idx) => (
                                <li key={idx}>
                                    <span className='dataset-source-main'>
                                        {fonte.url
                                            ? <a href={fonte.url} target="_blank" rel="noopener noreferrer" className='dataset-source-link'>{fonte.label}</a>
                                            : <strong>{fonte.label}</strong>}
                                        {fonte.periodo && <span className='dataset-source-period'>{fonte.periodo}</span>}
                                    </span>
                                    {fonte.detalhe && <span className='dataset-source-detail'>{fonte.detalhe}</span>}
                                </li>
                            ))}
                        </ul>
                    </DatasetSection>
                )}

                {(atualizacao || periodicidade) && (
                    <DatasetSection title="Atualização dos dados">
                        {periodicidade && <span className='dataset-badge'>{periodicidade}</span>}
                        {atualizacao && <p className='dataset-paragraph'>{atualizacao}</p>}
                    </DatasetSection>
                )}

                {/*
                  Dicionário de dados: o bloco abaixo é renderizado automaticamente
                  assim que `dicionario.arquivos` for preenchido em
                  src/Screens/DataPlataformScreens/datasets.js. Enquanto os arquivos
                  não existirem, a seção não aparece na tela.
                */}
                {temDicionario && (
                    <DatasetSection title="Dicionário">
                        {dicionario.descricao && <p className='dataset-paragraph'>{dicionario.descricao}</p>}
                        <div className='dataset-dictionary-files'>
                            {dicionario.arquivos.map((arquivo, idx) => (
                                <a
                                    key={idx}
                                    href={arquivo.url}
                                    download
                                    className='dataset-dictionary-file'
                                >
                                    <Download size={18} color="#4654A3" />
                                    <span>{arquivo.label}</span>
                                    {arquivo.formato && <span className='dataset-file-format'>{arquivo.formato}</span>}
                                </a>
                            ))}
                        </div>
                    </DatasetSection>
                )}
            </div>
        );
    }

    renderContatoCta() {
        return (
            <div className='dataset-cta'>
                <span className='dataset-cta-title'>Encontrou algo estranho nos dados?</span>
                <span className='dataset-cta-text'>Fale com nossa equipe para tirar dúvidas ou relatar um problema</span>
                <Link to="/contact" className='dataset-cta-button'>
                    Entrar em contato
                    <ChevronRight size={18} />
                </Link>
            </div>
        );
    }

    render() {
        const { results, buscou, page, count, pageSize } = this.state;
        const totalPages = Math.ceil(count / pageSize);
        const meta = this.getMeta();

        const isTelaEstado = typeof this.props.api.ufs === 'string' && this.props.api.ufs.includes('filtro-uf');

        return (
            <div className='container dataset-page'>
                {this.renderHeader(meta)}
                <div className='dataset-body'>
                    {this.renderSidebar()}
                    <main className='dataset-main'>
                        <h2 className='dataset-section-title'>Acesse os dados</h2>

                        <DataPlataformSearch
                            exercicios={this.state.exerciciosFiltrados}
                            exerciciosSelecionados={this.state.exerciciosSelecionados}
                            setExerciciosSelecionados={exerciciosSelecionados => this.setState({ exerciciosSelecionados })}
                            ufs={this.state.ufsFiltrados}
                            ufsSelecionadas={this.state.ufsSelecionadas}
                            setUfsSelecionadas={ufsSelecionadas => this.setState({ ufsSelecionadas })}
                            ufsHasMore={isTelaEstado ? false : this.state.ufsHasMore}
                            onUfsLoadMore={isTelaEstado ? undefined : this.handleUfsLoadMore}
                            onUfsSearch={this.handleUfsSearch}
                            contas={this.state.contas}
                            contasHasMore={this.getContasMode() === 'flat' ? false : this.state.contasHasMore}
                            onContasLoadMore={this.handleContasLoadMore}
                            contasSelecionadas={this.state.contasSelecionadas}
                            setContasSelecionadas={contasSelecionadas => this.setState({ contasSelecionadas })}
                            tiposDespesa={this.state.tiposDespesa}
                            tiposDespesaSelecionados={this.state.tiposDespesaSelecionados}
                            setTiposDespesaSelecionados={tiposDespesaSelecionados => this.setState({ tiposDespesaSelecionados })}
                            onContasSearch={this.handleContasSearch}
                            onExerciciosSearch={this.handleExerciciosSearch}
                            onSearch={this.handleSearch}
                            searchLoading={this.state.searchLoading}
                            isTelaEstado={isTelaEstado}
                            showTipoDespesaFilter={this.props.enableTipoDespesaFilter}
                            contaPlaceholder={this.props.contaPlaceholder}
                        />
                        {!buscou && (
                            <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                                Selecione os filtros acima para visualizar os dados.
                            </div>
                        )}
                        {this.renderChart()}
                        {results && results.length > 0 && (
                            <div style={{ display: 'flex', gap: 30, width: '100%' }}>
                                {/* <Button
                                    label="Fazer download"
                                    variant="primary"
                                    iconRight={<img src="/images/icons/download.svg" alt="Download" width={18} />}
                                    onClick={() => console.log('Download')}
                                /> */}
                                <Button
                                    label="Enviar por E-mail"
                                    variant="secondary"
                                    iconRight={<img src="/images/icons/letter_blue.svg" alt="E-mail" width={18} />}
                                    onClick={this.openEmailModal}
                                />
                            </div>
                        )}
                        {buscou && (
                            <div
                                style={{
                                    width: '100%',
                                    marginTop: 32,
                                    border: '1px solid #E8EAF4',
                                    borderRadius: 20,
                                    background: '#fff',
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                                    marginBottom: 32,
                                }}
                            >
                                {this.renderTable()}
                                {results && results.length > 0 && (
                                    <div style={{ padding: 16 }}>
                                        <Pagination
                                            currentPage={page}
                                            totalPages={totalPages}
                                            onPageChange={this.handlePageChange}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        {this.renderInfoSections(meta)}
                        {this.renderContatoCta()}
                    </main>
                </div>
                {this.renderEmailModal()}
                <Footer />
            </div>
        );
    }
}

export default DataPlataformListPage;